import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import StatusBadge from "@/components/StatusBadge";
import StarRating from "@/components/StarRating";
import WishlistButton from "@/components/WishlistButton";
import { resolveUrl } from "@/components/ImageUploader";
import BrandLogo from "@/components/BrandLogo";
import { Search, User, Heart, ShoppingBag, ArrowRight, Instagram, Facebook, Linkedin, Youtube, Twitter, MapPin } from "lucide-react";
import { toast } from "sonner";
import BrandStyle from "@/components/BrandStyle";

/**
 * Editorial template — ispirato al mockup TotalKlick:
 * Hero scuro · Ambientazioni grid · Come funziona la rete · Stats · Catalogo con filtri laterali · Footer scuro
 */
export default function StorefrontHomeEditorial({ data, cms, bestSellers, newsletterEmail, setNewsletterEmail, subscribeNewsletter }) {
  const { slug } = useParams();
  const { user } = useAuth();
  const cart = useCart();
  const { t } = useI18n();
  const { tenant, network, items } = data;
  const statuses = network.vertical_config?.availability_statuses || [];
  const brand = tenant.settings?.brand_kit || {};
  const social = tenant.settings?.social_links || {};
  const primary = brand.primary_color || "#0A0A0A";
  const accent = brand.secondary_color || "#C9A961";
  const fontHeading = brand.font_heading || "Cormorant Garamond";
  const fontBody = brand.font_body || "Inter";
  const hFont = { fontFamily: `"${fontHeading}", serif` };
  const heroImage = brand.hero_image || "https://images.unsplash.com/photo-1618219740975-d40978bb7378?w=1600";
  const heroTitle = cms?.sections?.[0]?.content?.title || brand.tagline || tenant.name;
  const heroSubtitle = cms?.sections?.[0]?.content?.subtitle || t("store_hero_default_subtitle");

  const [category, setCategory] = useState(null);
  const [priceFilter, setPriceFilter] = useState({ min: "", max: "" });
  const categories = network.vertical_config?.categories || [];

  const filtered = items.filter((i) => {
    if (category && i.categoria !== category) return false;
    if (priceFilter.min && i.prezzo_finale < parseFloat(priceFilter.min)) return false;
    if (priceFilter.max && i.prezzo_finale > parseFloat(priceFilter.max)) return false;
    return true;
  });

  const showrooms = (data.showrooms || []);
  const stats = { products: items.length, rivenditori: tenant.network_id ? 1 : 0, brands: 1 };

  return (
    <div className="min-h-screen bg-[#f6f3ee] text-[#1a1a1a]" data-testid="storefront-home" data-storefront="true" style={{ "--brand-primary": primary, "--brand-accent": accent }}>
      <BrandStyle brand={brand} />
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#f6f3ee]/90 border-b border-black/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
          <Link to={`/s/${slug}`} className="flex items-center gap-2.5">
            <BrandLogo
              logoUrl={brand.logo_url}
              fallbackText={tenant.name}
              resolveUrl={resolveUrl}
              className="h-10"
              textClassName="font-serif text-2xl tracking-tight"
            />
          </Link>
          <nav className="hidden lg:flex items-center gap-7 text-sm">
            {categories.slice(0, 5).map((c) => (
              <button key={c} onClick={() => { setCategory(c); document.getElementById("catalog")?.scrollIntoView({ behavior: "smooth" }); }}
                data-testid={`nav-cat-${c}`} className="hover:text-[var(--brand-accent)] transition-colors capitalize">{c}</button>
            ))}
            <a href="#contatti" className="hover:text-[var(--brand-accent)] transition-colors">{t("nav_contact")}</a>
          </nav>
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <Link to={`/s/${slug}/search`} className="w-9 h-9 rounded-full hover:bg-black/5 flex items-center justify-center" data-testid="header-search-link"><Search className="w-4 h-4" /></Link>
            <Link to={user?.role === "customer" ? "/account" : "/login"} className="w-9 h-9 rounded-full hover:bg-black/5 flex items-center justify-center" data-testid="header-account">
              <User className="w-4 h-4" />
            </Link>
            <Link to={user?.role === "customer" ? "/account" : "/login"} className="w-9 h-9 rounded-full hover:bg-black/5 flex items-center justify-center" data-testid="header-wishlist">
              <Heart className="w-4 h-4" />
            </Link>
            <button data-testid="header-cart" onClick={() => cart.setOpen(true)} className="relative w-9 h-9 rounded-full hover:bg-black/5 flex items-center justify-center">
              <ShoppingBag className="w-4 h-4" />
              {cart.count > 0 && <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full text-[10px] font-bold flex items-center justify-center text-white" style={{ background: accent }}>{cart.count}</span>}
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative text-white overflow-hidden" style={{ background: primary }} data-testid="editorial-hero">
        <div className="absolute inset-0">
          <img src={resolveUrl(heroImage)} alt="" className="w-full h-full object-cover opacity-90" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-28 lg:py-40 grid lg:grid-cols-2">
          <div>
            <h1 className="font-serif text-5xl lg:text-7xl leading-[1.05] tracking-tight mb-6" style={{ fontFamily: `"${fontHeading}", serif` }}>
              {heroTitle}
            </h1>
            <p className="text-lg text-white/85 max-w-md mb-8">{heroSubtitle}</p>
            <a href="#catalog" className="inline-flex items-center gap-2 px-7 h-12 text-sm uppercase tracking-wider font-medium text-white border border-white/40 hover:bg-white hover:text-[#1a1a1a] transition-colors">
              {t("store_explore_collection")}
            </a>
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="border-b border-black/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {[
            { t: t("store_pillar_consult"), icon: User },
            { t: t("store_pillar_design"), icon: Search },
            { t: t("store_pillar_delivery"), icon: ShoppingBag },
            { t: t("store_pillar_support"), icon: Heart },
          ].map((p, i) => {
            const I = p.icon;
            return (
              <div key={i} className="flex items-start gap-3" data-testid={`pillar-${i}`}>
                <div className="w-10 h-10 rounded-full border border-black/20 flex items-center justify-center flex-shrink-0" style={{ color: accent }}>
                  <I className="w-4 h-4" />
                </div>
                <div className="text-sm">{p.t}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Ambientazioni / Categorie */}
      {categories.length > 0 && (
        <section className="border-b border-black/10" data-testid="scenes-section">
          <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
            <h2 className="font-serif text-4xl mb-2" style={{ fontFamily: `"${fontHeading}", serif` }}>{t("store_scenes_title")}</h2>
            <p className="text-sm text-muted-foreground mb-8">{t("store_scenes_subtitle")}</p>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              {categories.slice(0, 5).map((cat, i) => {
                const sampleImg = items.find((it) => it.categoria === cat)?.immagini?.[0];
                return (
                  <button key={cat} onClick={() => { setCategory(cat); document.getElementById("catalog")?.scrollIntoView({ behavior: "smooth" }); }}
                    data-testid={`scene-${cat}`} className="group relative aspect-[3/4] overflow-hidden">
                    {sampleImg ? (
                      <img src={resolveUrl(sampleImg)} alt={cat} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    ) : (
                      <div className="absolute inset-0 bg-neutral-200" />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                    <div className="absolute bottom-3 left-3 text-white text-sm font-medium uppercase tracking-wider capitalize">{cat}</div>
                  </button>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Catalogo con filtri laterali */}
      <section id="catalog" className="border-b border-black/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
          <h2 className="font-serif text-4xl mb-8" style={{ fontFamily: `"${fontHeading}", serif` }}>{t("store_catalog_title")}</h2>
          <div className="grid lg:grid-cols-12 gap-8">
            <aside className="lg:col-span-3 lg:sticky lg:top-24 self-start space-y-6 text-sm">
              <div>
                <div className="overline text-muted-foreground mb-2">{t("store_categories")}</div>
                <div className="space-y-1">
                  <button onClick={() => setCategory(null)} data-testid="filter-cat-all" className={`block w-full text-left px-2 py-1 rounded-sm ${!category ? "bg-black text-white" : "hover:bg-black/5"}`}>{t("store_all")}</button>
                  {categories.map((c) => (
                    <button key={c} onClick={() => setCategory(c)} data-testid={`filter-cat-${c}`} className={`block w-full text-left px-2 py-1 rounded-sm capitalize ${category === c ? "bg-black text-white" : "hover:bg-black/5"}`}>{c}</button>
                  ))}
                </div>
              </div>
              <div>
                <div className="overline text-muted-foreground mb-2">{t("store_price")} (€)</div>
                <div className="flex gap-2">
                  <input data-testid="filter-min" type="number" placeholder="min" value={priceFilter.min} onChange={(e) => setPriceFilter({ ...priceFilter, min: e.target.value })} className="w-full h-9 px-2 rounded-sm border border-black/20 text-sm" />
                  <input data-testid="filter-max" type="number" placeholder="max" value={priceFilter.max} onChange={(e) => setPriceFilter({ ...priceFilter, max: e.target.value })} className="w-full h-9 px-2 rounded-sm border border-black/20 text-sm" />
                </div>
              </div>
              <div className="pt-3 border-t border-black/10 text-xs text-muted-foreground">{filtered.length} {t("prodotti")}</div>
            </aside>
            <div className="lg:col-span-9">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-10">
                {filtered.map((item) => (
                  <div key={item.sku_variante} className="group relative" data-testid={`product-card-${item.sku_variante}`}>
                    <Link to={`/s/${slug}/style/${item.sku_madre || item.sku_variante}`}>
                      <div className="relative overflow-hidden bg-white aspect-[4/5] mb-3">
                        {item.immagini?.[0] && <img src={resolveUrl(item.immagini[0])} alt={item.nome} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />}
                        <div className="absolute top-3 left-3"><StatusBadge status={item.stato_disponibilita} statuses={statuses} /></div>
                      </div>
                    </Link>
                    <div className="absolute top-3 right-3">
                      <WishlistButton sku={item.sku_variante} tenantSlug={slug} size="sm" />
                    </div>
                    <div className="overline text-muted-foreground mb-1 capitalize">{item.categoria}</div>
                    <Link to={`/s/${slug}/style/${item.sku_madre || item.sku_variante}`}>
                      <h3 className="font-serif text-xl mb-1" style={{ fontFamily: `"${fontHeading}", serif` }}>{item.nome}</h3>
                    </Link>
                    <div className="flex items-center justify-between">
                      <span className="font-medium">€ {item.prezzo_finale}</span>
                      <button onClick={() => cart.add(item.sku_variante, 1, { nome: item.nome, image: item.immagini?.[0], custom_attributes: item.custom_attributes_variante })}
                        data-testid={`add-to-cart-${item.sku_variante}`}
                        className="text-xs uppercase tracking-wider px-3 h-8 hover:bg-black hover:text-white border border-black/30 transition-colors">
                        {t("action_add")}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="text-white" style={{ background: primary }} data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 grid grid-cols-3 gap-6">
          <Stat n={`${stats.products}+`} label={t("prodotti")} accent={accent} fontHeading={fontHeading} />
          <Stat n="1" label={network.vertical_config?.terminology?.casa_madre_label?.toLowerCase() || "Brand"} accent={accent} fontHeading={fontHeading} />
          <Stat n="—" label={t("rivenditori")} accent={accent} fontHeading={fontHeading} />
        </div>
      </section>

      {/* Newsletter */}
      <section className="border-b border-black/10" data-testid="newsletter-section">
        <div className="max-w-3xl mx-auto px-6 lg:px-12 py-14 text-center">
          <h2 className="font-serif text-3xl mb-3" style={{ fontFamily: `"${fontHeading}", serif` }}>{t("store_newsletter_title")}</h2>
          <p className="text-sm text-muted-foreground mb-6">{t("store_newsletter_lead")}</p>
          <form onSubmit={subscribeNewsletter} className="flex gap-2 max-w-md mx-auto">
            <input data-testid="newsletter-email" type="email" required placeholder={t("store_newsletter_email")} value={newsletterEmail}
              onChange={(e) => setNewsletterEmail(e.target.value)} className="flex-1 h-12 px-4 border border-black/30 text-sm bg-transparent" />
            <button data-testid="newsletter-submit" className="px-6 h-12 text-white text-xs uppercase tracking-wider" style={{ background: primary }}>{t("store_newsletter_subscribe")}</button>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer id="contatti" className="text-white" style={{ background: primary }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-14 grid md:grid-cols-3 gap-8">
          <div>
            <BrandLogo
              logoUrl={brand.logo_url}
              fallbackText={tenant.name}
              resolveUrl={resolveUrl}
              className="h-10 mb-3"
              textClassName="font-serif text-2xl mb-3"
            />
            {brand.tagline && <p className="text-sm text-white/70 mb-3">{brand.tagline}</p>}
            {brand.footer_text && <p className="text-xs text-white/60 whitespace-pre-line">{brand.footer_text}</p>}
          </div>
          <div>
            <div className="overline text-white/60 mb-3">{t("store_legal")}</div>
            <Link to={`/s/${slug}/legal/privacy`} className="block text-sm text-white/80 hover:text-white">Privacy Policy</Link>
            <Link to={`/s/${slug}/legal/cookie`} className="block text-sm text-white/80 hover:text-white">Cookie Policy</Link>
            <Link to={`/s/${slug}/legal/terms`} className="block text-sm text-white/80 hover:text-white">{t("store_terms_conditions")}</Link>
          </div>
          <div>
            <div className="overline text-white/60 mb-3">{t("store_follow_us")}</div>
            <div className="flex gap-2">
              {Object.entries(social).filter(([, v]) => v).map(([net, url]) => {
                const I = { facebook: Facebook, instagram: Instagram, linkedin: Linkedin, youtube: Youtube, x_twitter: Twitter, tiktok: Twitter }[net] || Twitter;
                return <a key={net} href={url} target="_blank" rel="noreferrer" data-testid={`social-${net}`} className="w-9 h-9 flex items-center justify-center bg-white/10 hover:bg-white/20"><I className="w-4 h-4" /></a>;
              })}
            </div>
          </div>
        </div>
        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4 flex items-center justify-between text-xs text-white/50">
            <span>© 2026 {tenant.name}</span>
            <a href="/" className="hover:text-white">{t("store_powered_by")} TotalKlick</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

const Stat = ({ n, label, accent, fontHeading = "Cormorant Garamond" }) => (
  <div className="text-center" data-testid={`stat-${label}`}>
    <div className="font-serif text-5xl lg:text-6xl" style={{ fontFamily: `"${fontHeading}", serif`, color: accent }}>{n}</div>
    <div className="text-xs uppercase tracking-wider text-white/60 mt-1">{label}</div>
  </div>
);
